import React from 'react';
import { motion } from 'motion/react';
import { MapPin, ExternalLink, Car } from 'lucide-react';
import { BDAY_EASE } from '../theme';
import type { BirthdayContent } from '../content';
import { SectionBgImage, hasSectionBg } from './SectionBgImage';

function buildDirectionsUrl(name: string, address?: string): string {
  // Use the dedicated directions API endpoint so Google Maps opens in routing
  // mode (user's location → venue), not just a place view.
  const destination = address && address !== name
    ? `${name}, ${address}`
    : name;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
}

export function VenueSection({ content }: { content: BirthdayContent }) {
  const venue = content.venue;
  const hasAddress = venue.address && venue.address !== venue.name;
  // Show "Get Directions" whenever we have a maps link OR a meaningful venue name.
  // Always build a proper directions URL so it opens routing mode, not just a place view.
  const showDirections = Boolean(venue.mapsLink || venue.name);
  const directionsUrl = buildDirectionsUrl(venue.name, venue.address || undefined);

  const bg = content.sectionBackgrounds.venue;
  const active = hasSectionBg(bg);

  return (
    <section
      className="relative py-24 px-6 overflow-hidden"
      style={active ? undefined : { background: 'linear-gradient(180deg, #0f172a 0%, #1e3a5f 100%)' }}
    >
      <SectionBgImage bg={bg} />
      {/* glow blob */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-blue-600/15 blur-3xl pointer-events-none" />

      <div className="relative max-w-xl mx-auto">
        {/* heading */}
        <div className="text-center mb-10">
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: BDAY_EASE }}
            className="text-blue-400 text-xs font-bold uppercase tracking-[0.25em] mb-2"
          >
            📍 Location
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease: BDAY_EASE }}
            className="text-3xl sm:text-4xl font-bold text-white"
          >
            Venue
          </motion.h2>
        </div>

        {/* venue card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65, ease: BDAY_EASE }}
          className="bg-white/5 backdrop-blur-sm border border-blue-400/20 rounded-3xl overflow-hidden shadow-xl shadow-blue-900/30"
        >
          {/* venue image */}
          {venue.imageUrl && (
            <div className="h-52 overflow-hidden">
              <img
                src={venue.imageUrl}
                alt={venue.name}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-6 flex flex-col gap-4">
            {/* name + address */}
            <div>
              <div className="flex items-center gap-2 text-blue-400 text-xs font-bold uppercase tracking-widest mb-2">
                <MapPin size={14} />
                Venue
              </div>
              <h3 className="text-white font-bold text-xl">{venue.name}</h3>
              {hasAddress && (
                <p className="text-blue-200 text-sm mt-1">{venue.address}</p>
              )}
            </div>

            {/* parking */}
            {venue.parkingInstructions && (
              <div className="flex items-start gap-2 text-blue-300 text-sm bg-blue-500/10 rounded-xl px-4 py-3">
                <Car size={15} className="shrink-0 mt-0.5" />
                <span>{venue.parkingInstructions}</span>
              </div>
            )}

            {/* maps link — opens Google Maps in directions/routing mode */}
            {showDirections && (
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-blue-500 to-blue-700 text-white font-bold text-sm rounded-2xl hover:from-blue-400 hover:to-blue-600 transition-all shadow-lg"
              >
                <MapPin size={15} />
                Get Directions
                <ExternalLink size={13} />
              </a>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
